/**
 * Premium Page Component.
 * Clean and slim premium subscription page with embedded Stripe checkout.
 */

import React, { useState } from 'react';
import { StripeEmbeddedCheckout } from '@/components/StripeEmbeddedCheckout';
import { PlanSelection } from '@/components/PlanSelection';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut } from 'lucide-react';
import { useAuth } from '@/stores/supabaseAuthStore';
import {
  CheckoutLayout,
  CheckoutCard,
  CheckoutFeaturesList,
  CheckoutSecurityBadge,
  CheckoutFooter,
} from '@/components/checkout';

interface PremiumPageProps {
  onSuccess?: () => void;
}

export const PremiumPage: React.FC<PremiumPageProps> = ({ onSuccess }) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [step, setStep] = useState<'plan' | 'checkout'>('plan');
  const [selectedPlanId, setSelectedPlanId] = useState<string>('monthly');
  const [error, setError] = useState<string | null>(null);

  const handlePlanSelect = (planId: string) => {
    setSelectedPlanId(planId);
    setError(null);
  };

  const handlePlanContinue = () => {
    if (selectedPlanId) {
      setStep('checkout');
    }
  };

  const handleBackToPlan = () => {
    setStep('plan');
  };

  const handleSuccess = () => {
    onSuccess?.();
    navigate('/');
  };

  const handleError = (error: string) => {
    console.error('Payment error:', error);
    setError(error);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <CheckoutLayout
      title="Unlock Premium Features"
      subtitle="Get unlimited access to all AI companions, advanced features, and priority support."
    >
      {/* Floating Sign Out Button */}
      <button
        onClick={handleSignOut}
        className="fixed top-4 left-4 z-50 flex items-center space-x-2 px-4 py-2 text-kind-text-secondary hover:text-kind-text-primary hover:bg-white/50 dark:hover:bg-kind-container/50 rounded-lg transition-colors backdrop-blur-sm border border-gray-200 dark:border-white/10"
      >
        <LogOut className="h-4 w-4" />
        <span>Sign Out</span>
      </button>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Features List */}
        <div className="space-y-6">
          <CheckoutFeaturesList />
          <CheckoutSecurityBadge />
        </div>

        {/* Payment Form */}
        <div className="lg:sticky lg:top-8">
          {step === 'plan' && (
            <CheckoutCard>
              <PlanSelection
                selectedPlanId={selectedPlanId}
                onPlanSelect={handlePlanSelect}
                onContinue={handlePlanContinue}
              />
            </CheckoutCard>
          )}

          {step === 'checkout' && (
            <CheckoutCard>
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={handleBackToPlan}
                  className="text-kind-text-secondary hover:text-kind-text-primary transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <h2 className="text-2xl font-bold text-kind-text-primary">Complete Your Payment</h2>
              </div>
              <p className="text-kind-text-secondary mb-6">
                You're almost there! Complete your payment to start your subscription.
              </p>

              {error && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-6">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <StripeEmbeddedCheckout
                selectedPlanId={selectedPlanId}
                showPlanSelection={false}
                onSuccess={handleSuccess}
                onError={handleError}
                className="h-fit"
              />
            </CheckoutCard>
          )}
        </div>
      </div>

      <CheckoutFooter />
    </CheckoutLayout>
  );
};

export default PremiumPage;
